<template>
  <div class="input-multiple-files">
    <div
      class="drop-zone"
      :class="{ 'is-dragover': isDragover }"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
      @click="triggerFileInput"
    >
      <input
        type="file"
        ref="fileInput"
        multiple
        :accept="acceptedFileTypes"
        @change="handleFileSelect"
        style="display: none"
      />
      <div class="drop-zone-content">
        <v-icon large color="primary">mdi-cloud-upload</v-icon>
        <div class="text-center mt-2">
          <span class="text-body-1">Arraste arquivos aqui ou clique para selecionar</span>
          <div class="text-caption grey--text">
            Formatos aceitos: {{ acceptedFileTypes }}
          </div>
        </div>
      </div>
    </div>

    <!-- Lista de arquivos -->
    <div v-if="files.length > 0" class="files-list mt-4">
      <v-list>
        <v-list-item v-for="(file, index) in files" :key="index">
          <v-list-item-icon>
            <v-icon color="primary">mdi-file</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ file.name }}</v-list-item-title>
            <v-list-item-subtitle>{{ formatFileSize(file.size) }}</v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action class="d-flex align-center" style="min-width: 80px;">
            <div class="d-flex align-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    class="mr-1"
                    v-bind="attrs"
                    v-on="on"
                    @click="downloadFile(file)"
                  >
                    <v-icon color="primary" small>mdi-download</v-icon>
                  </v-btn>
                </template>
                <span>Baixar arquivo</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    @click="removeFile(index)"
                  >
                    <v-icon color="error" small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Remover arquivo</span>
              </v-tooltip>
            </div>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InputMultipleFiles',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    acceptedFileTypes: {
      type: String,
      default: '.pdf,.jpg,.jpeg,.png'
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024 // 10MB em bytes
    }
  },
  data() {
    return {
      files: [],
      isDragover: false
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue && Array.isArray(newValue)) {
          this.files = [...newValue]
        }
      }
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    handleFileSelect(event) {
      const newFiles = Array.from(event.target.files)
      this.processFiles(newFiles)
      // Limpa o input para permitir selecionar o mesmo arquivo novamente
      event.target.value = ''
    },
    handleDragOver(event) {
      this.isDragover = true
    },
    handleDragLeave(event) {
      this.isDragover = false
    },
    handleDrop(event) {
      this.isDragover = false
      const newFiles = Array.from(event.dataTransfer.files)
      this.processFiles(newFiles)
    },
    processFiles(newFiles) {
      const validFiles = newFiles.filter(file => {
        // Verifica o tipo do arquivo
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
        const acceptedTypes = this.acceptedFileTypes.split(',')
        const isValidType = acceptedTypes.some(type => 
          type.toLowerCase() === fileExtension || type.toLowerCase() === file.type
        )

        // Verifica o tamanho do arquivo
        const isValidSize = file.size <= this.maxFileSize

        if (!isValidType) {
          this.$emit('error', `Tipo de arquivo não suportado: ${file.name}`)
        }
        if (!isValidSize) {
          this.$emit('error', `Arquivo muito grande: ${file.name}`)
        }

        return isValidType && isValidSize
      })

      this.files = [...this.files, ...validFiles]
      this.$emit('input', this.files)
    },
    removeFile(index) {
      this.files.splice(index, 1)
      this.$emit('input', this.files)
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    downloadFile(file) {
      // Se o arquivo for um Blob (arquivo baixado do storage)
      if (file instanceof Blob) {
        try {
          // Verifica se é um dispositivo iOS
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
          
          if (isIOS) {
            // Para iOS, abrimos em uma nova aba
            const reader = new FileReader();
            reader.onload = function(e) {
              const newWindow = window.open();
              newWindow.document.write(`
                <html>
                  <head>
                    <title>${file.name}</title>
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  </head>
                  <body style="margin:0;padding:0;display:flex;justify-content:center;align-items:center;min-height:100vh;">
                    <iframe src="${e.target.result}" style="width:100%;height:100vh;border:none;"></iframe>
                  </body>
                </html>
              `);
            };
            reader.readAsDataURL(file);
          } else {
            // Para outros dispositivos, usamos o método padrão
            const url = window.URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.name || 'download';
            link.target = '_blank'; // Abre em nova aba para melhor compatibilidade
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }
        } catch (error) {
          console.error('Erro ao baixar arquivo:', error);
          // Fallback: tenta abrir em nova aba
          const reader = new FileReader();
          reader.onload = function(e) {
            window.open(e.target.result, '_blank');
          };
          reader.readAsDataURL(file);
        }
      }
    }
  }
}
</script>

<style scoped>
.input-multiple-files {
  width: 100%;
}

.drop-zone {
  border: 2px dashed #ccc;
  border-radius: 4px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.drop-zone:hover {
  border-color: var(--v-primary-base);
  background-color: rgba(var(--v-primary-base), 0.05);
}

.is-dragover {
  border-color: var(--v-primary-base);
  background-color: rgba(var(--v-primary-base), 0.1);
}

.drop-zone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.files-list {
  max-height: 300px;
  overflow-y: auto;
}

.v-list-item {
  transition: background-color 0.2s ease;
}

.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.v-btn {
  transition: transform 0.2s ease;
}

.v-btn:hover {
  transform: scale(1.1);
}

.v-btn.v-btn--icon.v-size--x-small {
  width: 28px;
  height: 28px;
}

.v-list-item-action {
  margin-left: 0 !important;
}
</style> 